import { formatToLongDate } from "../../../utils/helpers.ts";
import { annualAmountToRoundedMonthly, appraisalCompensationSummary } from "./appraisal-annexure-utils.ts";
import type { AppraisalLetterPreviewData } from "./appraisal-letter-preview.ts";
import type { AppraisalLetterType } from "./types.ts";
import { createSqeAppraisalTermsHtml } from "./sqe-appraisal-terms.ts";

const number = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const SQE_LETTER_SUBJECTS: Record<AppraisalLetterType, string> = {
  APPRAISAL: "Letter of Salary Increment with SQE Labs",
  PROMOTION: "Letter of Promotion with SQE Labs",
  APPRAISAL_WITH_PROMOTION: "Letter of Salary Revision and Promotion with SQE Labs",
};

function escapeHtml(value: string | number | null | undefined): string {
  const replacements: Record<string, string> = {
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  };
  return String(value ?? "-").replace(/[&<>"']/g, (character) => replacements[character]);
}

function amountRow(label: string, monthly: number, annual: number): string {
  return `<tr><td>${escapeHtml(label)}</td><td>${number.format(monthly)}</td><td>${number.format(annual)}</td></tr>`;
}

function totalRow(label: string, monthly: number, annual: number, className = "subtotal"): string {
  return `<tr class="${className}"><th scope="row">${escapeHtml(label)}</th><td>${number.format(monthly)}</td><td>${number.format(annual)}</td></tr>`;
}

function groupRow(label: string): string {
  return `<tr class="group"><th colspan="3" scope="rowgroup">${escapeHtml(label)}</th></tr>`;
}

export function createSqeAppraisalLetterHtml(
  data: AppraisalLetterPreviewData,
  annualCtcWords: string,
  includeToolbar = false,
): string {
  const summary = data.compensationSummary ?? appraisalCompensationSummary(data.annexureValues);
  const component = summary.components;
  const publicUrl = process.env.PUBLIC_URL ?? "";
  const letterheadUrl = new URL(`${publicUrl.replace(/\/$/, "")}/assets/sqe-appraisal-letterhead_bg.png`, window.location.origin).href;
  const paginationUrl = new URL(`${publicUrl.replace(/\/$/, "")}/templates/appraisal-v2-dynamic.js?v=20261013-1`, window.location.origin).href;
  const employeeName = data.employeeName.trim() || data.personalDetail?.empName || "-";
  const recipientName = data.personalDetail?.firstName?.trim() || employeeName;
  const designation = data.basicValues.letterType === "APPRAISAL"
    ? data.personalDetail?.designation
    : data.basicValues.designation || data.personalDetail?.designation;
  const effectiveDate = formatToLongDate(data.basicValues.effectiveDate, "-");
  const issueDate = formatToLongDate(data.basicValues.letterIssueDate, "-");
  const isPromotion = data.basicValues.letterType !== "APPRAISAL";
  const subject = SQE_LETTER_SUBJECTS[data.basicValues.letterType];
  const annualFixed = component.annualFixedComponent;
  const rows = [
    groupRow("A. Essentials"),
    amountRow("Basic Salary", component.basic, component.basic * 12),
    amountRow("Provident Fund (Employee contribution)", component.pfEmployeePart, component.pfEmployeePart * 12),
    ...(summary.esicEligible ? [amountRow("ESIC (Employee contribution)", component.esicEmployeePart, component.esicEmployeePart * 12)] : []),
    amountRow("Special Allowance", component.specialAllowance, component.specialAllowance * 12),
    groupRow("B. Other Allowances"),
    amountRow("House Rent Allowance", component.houseRentAllowance, component.houseRentAllowance * 12),
    amountRow("Internet Allowance", component.internetReimbursement, component.internetReimbursement * 12),
    totalRow("TOTAL (A + B)", summary.monthlyGross, summary.annualGross),
    groupRow("C. Annual Fixed Component"),
    amountRow("Annual Fixed Component", annualAmountToRoundedMonthly(annualFixed), annualFixed),
    totalRow("TOTAL (C)", annualAmountToRoundedMonthly(annualFixed), annualFixed),
    groupRow("D. Other Benefits"),
    amountRow("Gratuity", component.gratuity, Math.round(component.basic / 2)),
    amountRow("Provident Fund (Employer contribution)", component.pfEmployerPart, component.pfEmployerPart * 12),
    ...(summary.esicEligible ? [amountRow("ESIC (Employer contribution)", component.esicEmployerPart, component.esicEmployerPart * 12)] : []),
    totalRow("TOTAL (D)", summary.monthlyBenefits, summary.annualBenefits),
    totalRow("Gross CTC (A + B + C + D)", summary.monthlyCtc, summary.annualCtc, "grand-total"),
  ].join("");
  const toolbar = includeToolbar
    ? '<div class="preview-toolbar"><button type="button" onclick="window.print()">Print / Save as PDF</button></div>'
    : "";
  const terms = createSqeAppraisalTermsHtml(employeeName, data.personalDetail?.currentLocation);

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"/><title>Appraisal Letter - ${escapeHtml(employeeName)}</title>
<link rel="preload" as="image" href="${escapeHtml(letterheadUrl)}"/>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 24px 0; background: #eef1f5; color: #263447; font: 14px/1.5 Arial, sans-serif; }
  .preview-toolbar { width: 794px; margin: 0 auto 16px; text-align: right; }
  .preview-toolbar button { border: 0; border-radius: 6px; padding: 10px 16px; background: #33517d; color: white; cursor: pointer; }
  body > .header, body > .footer, body > .page { display: none; }
  .pdf-page { position: relative; width: 794px; height: 1123px; margin: 0 auto 20px; background: white; box-shadow: 0 3px 18px #00000018; overflow: hidden; }
  .pdf-page > .header { position: absolute; display: block; top: 0; left: 0; width: 794px; height: 127px; z-index: 0; }
  .letterhead { position: absolute; top: 0; left: 0; width: 794px; height: 1123px; max-width: none; }
  .pdf-page > .footer { position: absolute; display: flex; align-items: flex-end; justify-content: center; left: 48px; right: 48px; bottom: 44px; height: 70px; padding-bottom: 12px; color: white; text-align: center; font-size: 10px; }
  .pdf-page-content { position: absolute; left: 64px; right: 64px; z-index: 1; overflow: hidden; }
  .pdf-page-number { position: absolute; left: 0; right: 0; bottom: 88px; color: #171717; text-align: center; font-size: 16px; line-height: 22px; z-index: 1; }
  .pdf-page-number .page-word { color: #777; font-size: 13px; letter-spacing: 0.35em; }
  .letter-body { padding-top: 0; }
  .letter-body p { margin: 0 0 21px; }
  .letter-body .subject { margin-top: 27px; font-weight: 700; }
  .letter-body .signature { margin-top: 48px; }
  .letter-body .signature strong { display: block; margin-top: 38px; }
  .letter-body .confidential-note { margin-top: 48px; font-size: 11px; font-weight: 700; }
  .annexure-title { margin: 0 0 20px; text-align: center; font-size: 18px; color: #263447; }
  .employee-details { display: grid; grid-template-columns: 1fr 1fr; gap: 7px 20px; margin-bottom: 24px; font-size: 12px; }
  .employee-details div { display: flex; gap: 8px; }
  .employee-details strong { min-width: 85px; }
  .ctc-title { margin: 0 0 10px; text-align: center; font-size: 15px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { padding: 6px 9px; border: 1px solid #b9c6d4; text-align: left; }
  td:nth-child(n+2), thead th:nth-child(n+2) { text-align: right; white-space: nowrap; }
  thead { background: #33517d; color: white; }
  .group { background: #e7eef6; color: #233f63; }
  .group th { text-align: left; }
  .subtotal { background: #f4f7fa; font-weight: 700; }
  .grand-total { background: #33517d; color: white; font-weight: 700; }
  .annexure-words { margin-top: 18px; font-size: 12px; }
  .terms { margin: 0 18px; font-size: 14px; line-height: 1.4; color: #263447; }
  .terms > p { margin: 0 0 12px; }
  .terms > ol { margin: 0; padding-left: 22px; list-style: none; }
  .terms > ol > li { padding-left: 3px; margin: 0 0 12px; list-style: none; }
  .terms h4 { margin: 0 0 4px; font-size: 14px; }
  .term-section-number { margin-right: 4px; }
  .terms li > p { margin: 0 0 7px; }
  .sub-bullet { display: grid; grid-template-columns: 32px 1fr; gap: 4px; }
  .sub-bullet > span:last-child { overflow-wrap: anywhere; }
  .terms h3 { font-size: 13px; }
  .terms table { margin: 14px 0; }
  .terms table td { border: 0; padding: 5px; }
  @media print { body { padding: 0; background: white; } .preview-toolbar { display: none; } .pdf-page { margin: 0; box-shadow: none; break-after: page; } .pdf-page:last-child { break-after: auto; } }
</style></head>
<body data-letter-format="sqe">
${toolbar}
<header class="header">
  <img class="letterhead" src="${escapeHtml(letterheadUrl)}" alt="" aria-hidden="true"/>
</header>
<footer class="footer">India Office: SQE Labs Inc., #627, Tower A, Bestech Business Towers, Sector 66, Mohali, Punjab, India</footer>
<div class="page">
  <main class="letter-body">
    <p><strong>Dated:</strong> ${escapeHtml(issueDate)}</p>
    <p>To,<br/>${escapeHtml(data.salutation)} ${escapeHtml(employeeName)}</p>
    <p class="subject">Subject: ${escapeHtml(subject)}</p>
    <p>Dear ${escapeHtml(recipientName)},</p>
    <p>Consequent to the review of your performance, we are pleased to inform you that your revised Annual CTC will be Rs. ${number.format(summary.annualCtc)}/- effective ${escapeHtml(effectiveDate)}. The salary structure is detailed in Annexure I.</p>
    ${isPromotion ? `<p>Your revised designation will be ${escapeHtml(designation)}.</p>` : ""}
    <p>We greatly appreciate and look forward to your continued contribution to our organization.</p>
    <p class="signature">For: SQE Labs Inc.<strong>Chief People Officer</strong></p>
    <p class="confidential-note">Note: Salary-related information is Company Confidential for the sole use of the employee and cannot be shared with any other person directly or indirectly. Violation to this will be treated as a breach of employment contract and will be dealt with accordingly.</p>
  </main>
</div>
<div class="page">
  <main>
    <h1 class="annexure-title">Annexure I</h1>
    <div class="employee-details">
      <div><strong>Name</strong><span>${escapeHtml(employeeName)}</span></div>
      <div><strong>Designation</strong><span>${escapeHtml(designation)}</span></div>
      <div><strong>Effective</strong><span>${escapeHtml(effectiveDate)}</span></div>
      <div><strong>Location</strong><span>${escapeHtml(data.personalDetail?.currentLocation)}</span></div>
    </div>
    <h2 class="ctc-title">CTC Annexure</h2>
    <table aria-label="SQE appraisal compensation annexure">
      <thead><tr><th scope="col">Components</th><th scope="col">Monthly (In Rs.)</th><th scope="col">Annual (In Rs.)</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <p class="annexure-words"><strong>Annual CTC (In words):</strong> ${escapeHtml(annualCtcWords)}</p>
  </main>
 </div>
<div class="page"><div class="terms">${terms}</div></div>
<script src="${escapeHtml(paginationUrl)}"></script>
</body></html>`;
}
